import clsx from 'clsx';
import type { ChangeEvent, JSX } from 'react';
import type { AssetType, UnitAvailabilityStatus } from '@/lib/types/property';
import { ASSET_TYPE_LABELS, ASSET_TYPE_OPTIONS } from '@/lib/constants/propertyOptions';
import type { PublicPropertyFilterState } from '@/lib/types/publicProperty';

const READINESS_LABELS = Object.freeze<Record<UnitAvailabilityStatus, string>>({
  available: 'Available',
  unavailable: 'Unavailable',
  leased: 'Leased',
});

const READINESS_ORDER: UnitAvailabilityStatus[] = ['available', 'unavailable', 'leased'];

export const PUBLIC_ASSET_TYPE_LABELS = ASSET_TYPE_LABELS;
export const PUBLIC_READINESS_LABELS = READINESS_LABELS;
export const PUBLIC_READINESS_ORDER = READINESS_ORDER;

export interface PublicPropertyFilterControlsProps {
  value: PublicPropertyFilterState;
  onChange: (next: PublicPropertyFilterState) => void;
  submarketOptions: string[];
  assetTypeOptions?: AssetType[];
  className?: string;
}

export function PublicPropertyFilterControls({
  value,
  onChange,
  submarketOptions,
  assetTypeOptions = ASSET_TYPE_OPTIONS,
  className,
}: PublicPropertyFilterControlsProps): JSX.Element {
  const normalizedSubmarkets = Array.from(new Set(submarketOptions))
    .filter((entry): entry is string => entry.length > 0)
    .sort((a, b) => a.localeCompare(b));
  const assetsToRender = [...assetTypeOptions].sort((a, b) => {
    const aLabel = ASSET_TYPE_LABELS[a];
    const bLabel = ASSET_TYPE_LABELS[b];
    return aLabel.localeCompare(bLabel);
  });

  const handleSubmarketToggle = (target: string) => {
    const next = toggleString(value.submarkets, target);
    onChange({ ...value, submarkets: next });
  };

  const handleAssetToggle = (target: AssetType) => {
    const next = toggleEnum(value.assetTypes, target);
    onChange({ ...value, assetTypes: next });
  };

  const handleReadinessToggle = (target: UnitAvailabilityStatus) => {
    const next = toggleEnum(value.readinessStates, target);
    onChange({ ...value, readinessStates: next });
  };

  const handleRangeChange = (
    key: 'rentableSquareFeet' | 'rentPerSquareFoot',
    bound: 'min' | 'max',
    input: string,
  ) => {
    const nextRange = { ...value[key] };
    const trimmed = input.trim();
    if (trimmed.length === 0) {
      nextRange[bound] = null;
      onChange({ ...value, [key]: nextRange });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return;
    }
    nextRange[bound] = parsed;
    onChange({ ...value, [key]: nextRange });
  };

  const handleAvailabilityChange = (bound: 'start' | 'end', input: string) => {
    const nextAvailability = { ...value.availability };
    nextAvailability[bound] = input.trim().length === 0 ? null : input;
    onChange({ ...value, availability: nextAvailability });
  };

  const handleDivisibleChange = (selection: boolean | null) => {
    onChange({ ...value, potentiallyDivisible: selection });
  };

  return (
    <div className={clsx('flex flex-col gap-6', className)}>
      <FilterSection
        title="Submarket"
        description="Focus on the neighbourhoods that align with your team’s commute and partner network."
      >
        {normalizedSubmarkets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {normalizedSubmarkets.map((option) => (
              <FilterChip
                key={option}
                label={option}
                selected={value.submarkets.includes(option)}
                onToggle={() => {
                  handleSubmarketToggle(option);
                }}
              />
            ))}
          </div>
        ) : (
          <p className="text-xs text-text-primary/70">
            Submarket filters will appear once current listings finish loading.
          </p>
        )}
      </FilterSection>

      <FilterSection
        title="Asset type"
        description="Choose the building typology best suited for your operations."
      >
        <div className="flex flex-wrap gap-2">
          {assetsToRender.map((option) => (
            <FilterChip
              key={option}
              label={ASSET_TYPE_LABELS[option]}
              selected={value.assetTypes.includes(option)}
              onToggle={() => {
                handleAssetToggle(option);
              }}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Rentable square feet" description="Filter by target floor plate.">
        <RangeInputRow>
          <RangeInput
            id="rentable-min"
            label="Min"
            suffix="sf"
            value={toNumberInputValue(value.rentableSquareFeet.min)}
            onChange={(event) => {
              handleRangeChange('rentableSquareFeet', 'min', event.target.value);
            }}
          />
          <RangeInput
            id="rentable-max"
            label="Max"
            suffix="sf"
            value={toNumberInputValue(value.rentableSquareFeet.max)}
            onChange={(event) => {
              handleRangeChange('rentableSquareFeet', 'max', event.target.value);
            }}
          />
        </RangeInputRow>
      </FilterSection>

      <FilterSection
        title="Net rent per square foot"
        description="Budget guardrails for PSF rates."
      >
        <RangeInputRow>
          <RangeInput
            id="rent-psf-min"
            label="Min"
            prefix="$"
            value={toNumberInputValue(value.rentPerSquareFoot.min)}
            onChange={(event) => {
              handleRangeChange('rentPerSquareFoot', 'min', event.target.value);
            }}
          />
          <RangeInput
            id="rent-psf-max"
            label="Max"
            prefix="$"
            value={toNumberInputValue(value.rentPerSquareFoot.max)}
            onChange={(event) => {
              handleRangeChange('rentPerSquareFoot', 'max', event.target.value);
            }}
          />
        </RangeInputRow>
      </FilterSection>

      <FilterSection
        title="Availability window"
        description="Target move-in timelines aligned with your project plan."
      >
        <RangeInputRow>
          <DateInput
            id="availability-start"
            label="Starts"
            value={toDateInputValue(value.availability.start)}
            onChange={(event) => {
              handleAvailabilityChange('start', event.target.value);
            }}
          />
          <DateInput
            id="availability-end"
            label="Ends"
            value={toDateInputValue(value.availability.end)}
            onChange={(event) => {
              handleAvailabilityChange('end', event.target.value);
            }}
          />
        </RangeInputRow>
      </FilterSection>

      <FilterSection
        title="Readiness"
        description="See whether suites are available, unavailable, or leased."
      >
        <div className="flex flex-wrap gap-2">
          {READINESS_ORDER.map((option) => (
            <FilterChip
              key={option}
              label={READINESS_LABELS[option]}
              selected={value.readinessStates.includes(option)}
              onToggle={() => {
                handleReadinessToggle(option);
              }}
            />
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Programs">
        <label className="flex items-center gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm font-medium text-text-primary">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
            checked={value.incentivesOnly}
            onChange={(event) => {
              onChange({ ...value, incentivesOnly: event.target.checked });
            }}
          />
          Show incentives only
        </label>
      </FilterSection>

      <FilterSection title="Divisibility">
        <div className="grid grid-cols-3 gap-2">
          <SegmentedButton
            label="Any"
            selected={value.potentiallyDivisible === null}
            onClick={() => {
              handleDivisibleChange(null);
            }}
          />
          <SegmentedButton
            label="Divisible"
            selected={value.potentiallyDivisible === true}
            onClick={() => {
              handleDivisibleChange(true);
            }}
          />
          <SegmentedButton
            label="Single"
            selected={value.potentiallyDivisible === false}
            onClick={() => {
              handleDivisibleChange(false);
            }}
          />
        </div>
      </FilterSection>
    </div>
  );
}

function FilterSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  const hasDescription = typeof description === 'string' && description.trim().length > 0;
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
        {title}
      </legend>
      {hasDescription ? <p className="text-xs text-text-primary/70">{description}</p> : null}
      {children}
    </fieldset>
  );
}

function FilterChip({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={clsx(
        'inline-flex items-center justify-center rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition',
        selected
          ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
          : 'border-surface-subtle bg-white text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/10',
      )}
    >
      {label}
    </button>
  );
}

function SegmentedButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'rounded-2xl border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition',
        selected
          ? 'border-brand-primary bg-brand-primary text-text-inverted'
          : 'border-surface-subtle bg-white text-brand-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
      )}
    >
      {label}
    </button>
  );
}

function RangeInputRow({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function RangeInput({
  id,
  label,
  prefix,
  suffix,
  value,
  onChange,
}: {
  id: string;
  label: string;
  prefix?: string;
  suffix?: string;
  value: number | '';
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element {
  return (
    <label
      htmlFor={id}
      className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/60"
    >
      {label}
      <div className="flex items-center gap-2 rounded-2xl border border-surface-subtle bg-white px-3 py-2.5 md:py-2">
        {typeof prefix === 'string' && prefix.length > 0 ? (
          <span className="text-text-primary/70">{prefix}</span>
        ) : null}
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={onChange}
          className="w-full border-none bg-transparent text-base font-semibold text-brand-primary outline-none md:text-sm"
        />
        {typeof suffix === 'string' && suffix.length > 0 ? (
          <span className="text-text-primary/70">{suffix}</span>
        ) : null}
      </div>
    </label>
  );
}

function DateInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element {
  return (
    <label
      htmlFor={id}
      className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/60"
    >
      {label}
      <input
        id={id}
        type="date"
        value={value}
        onChange={onChange}
        className="w-full rounded-2xl border border-surface-subtle bg-white px-3 py-2.5 text-base font-semibold text-brand-primary outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 md:py-2 md:text-sm"
      />
    </label>
  );
}

function toggleString(values: string[], value: string): string[] {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
}

function toggleEnum<T extends string>(values: T[], value: T): T[] {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }
  return [...values, value];
}

function toNumberInputValue(value?: number | null): number | '' {
  return typeof value === 'number' ? value : '';
}

function toDateInputValue(value?: string | null): string {
  return typeof value === 'string' ? value : '';
}
